import { useState, useEffect, useContext, useMemo } from 'react';
import { UserContext } from './UserContext';
import { isAdminType } from './utils/roleUtils.js';
import Sidebar from './Sidebar.js';
import { Card, Image, Row, Col, Container, Form, Button, Modal, Table, Pagination } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CompanyAddGeneral from './CompanyAddGeneral.js';
import api from './api/axiosInstance.js';
import NavTabs from './NavTabs.js';
import icSucess from './assets/img/ic-celebration-circle.svg';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { TicketCountContext } from './TicketCountContext';
import { exportCompanyPdf } from './api/exportCompanyPdf.js';

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const FIELD_LABELS = {
    title: 'Company Name',
    url: 'Website',
    logo: 'Logo',
    accessLevel: 'Access Level',
    bestPlacetoWorkRating: 'Best Place to Work Rating',
    bestPlacetoWorkDescription: 'Best Place to Work Description',
    activityStatus: 'Activity Status',
    address: 'Address',
    address2: 'Address 2',
    country: 'Country',
    country2: 'Country 2',
    city: 'City',
    city2: 'City 2',
    cityRegion: 'City Region',
    zip: 'ZIP Code',
    zip2: 'ZIP Code 2',
    phone: 'Phone',
    phone2: 'Phone 2',
    childUrl: 'Child URL',
    childUrl2: 'Child URL 2',
    otherInformation: 'Other Information',
    otherInformation2: 'Other Information 2',
    latitude: 'Latitude',
    longitude: 'Longitude',
    latitude2: 'Latitude 2',
    longitude2: 'Longitude 2',
    legalName: 'Legal Name',
    vat: 'VAT',
    duns: 'DUNS',
    globalUltimate: 'Global Ultimate',
    globalSize: 'Global Size',
    localSize: 'Local Size',
    rating: 'Rating',
    ratingSource: 'Rating Source',
    industries: 'Industries',
    businessPurpose: 'Business Purpose',
    localActivities: 'Local Activities',
    localStaffing: 'Local Staffing',
    businessTypeOther: 'Business Type Other',
    isFortune: 'Fortune 1000',
    fortuneRank: 'Fortune Rank',
    fortuneYear: 'Fortune Year',
    fortuneRevenue: 'Fortune Revenue',
    fortunePercentChange: 'Fortune % Change',
    fortuneProfit: 'Fortune Profit',
};

const CompanyEditHistory = () => {
    const { id, cityId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const { ticketCount } = useContext(TicketCountContext);

    const [generalData, setGeneralData] = useState({
        logo: '',
        logoPreview: '',
        title: '',
        accessLevel: '',
        url: '',
        activityStatus: false,
    });
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [originalLogoUrl, setOriginalLogoUrl] = useState('');
    const [forceRerenderKey, setForceRerenderKey] = useState(0);
    const [errors, setErrors] = useState({});
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('');
    const [activityStatus, setActivityStatus] = useState(true);
    const [isLoadingCompany, setIsLoadingCompany] = useState(true);

    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [cityName, setCityName] = useState('');
    useEffect(() => {
        if (!cityId) return;
        api.get(`/api/cities/${cityId}`)
            .then(res => setCityName(res.data?.name || ''))
            .catch(err => console.error('Error fetching city for breadcrumb:', err));
    }, [cityId]);

    const cityBasePath = cityId
        ? location.pathname.includes('/city-edit-companies/')
            ? `/city-edit-companies/${cityId}/company/:id`
            : `/city-edit-jobs/${cityId}/company/:id`
        : null;
    const companyNavItems = cityBasePath
        ? [
            { to: cityBasePath, eventKey: 'companyInfo', label: 'Company Info', end: true },
            { to: `${cityBasePath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info' },
            { to: `${cityBasePath}/users`, eventKey: 'users', label: 'Users' },
            { to: `${cityBasePath}/history`, eventKey: 'history', label: 'History of changes' },
        ]
        : [
            { to: '/company-edit/:id', eventKey: 'companyInfo', label: 'Company Info' },
            { to: '/company-edit-hiring/:id', eventKey: 'hiringInfo', label: 'Hiring Info' },
            { to: '/company-edit-users/:id', eventKey: 'users', label: 'Users' },
            { to: '/company-edit-history/:id', eventKey: 'history', label: 'History of changes' },
        ];

    useEffect(() => {
        const fetchCompany = async () => {
            setIsLoadingCompany(true);
            try {
                const res = await api.get(`/api/companies/${id}`);
                const data = res.data;
                setActivityStatus(data.activityStatus ?? true);
                setOriginalLogoUrl(data.logo || '');
                setLogoPreview(data.logo || '');
                setGeneralData({
                    title: data.title || '',
                    url: data.url || '',
                    logo: data.logo || '',
                    logoPreview: data.logo || '',
                    accessLevel: data.accessLevel || '',
                    bestPlacetoWorkRating: data.bestPlacetoWorkRating || '',
                    bestPlacetoWorkDescription: data.bestPlacetoWorkDescription || '',
                    activityStatus: data.activityStatus ?? false,
                    createdAt: data.created_at || '',
                    updatedAt: data.updatedAt || '',
                    accountApprovalStatus: data.accountApprovalStatus || '',
                });
            } catch (err) {
                console.error('Error fetching company:', err);
            } finally {
                setIsLoadingCompany(false);
            }
        };
        fetchCompany();
    }, [id]);

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoadingHistory(true);
            try {
                const res = await api.get(`/api/companies/${id}/history`);
                setHistory(res.data || []);
            } catch (err) {
                console.error('Error fetching company history:', err);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        fetchHistory();
    }, [id]);

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
        if (name === 'logo' && value instanceof File) {
            setLogo(value);
            const fileUrl = URL.createObjectURL(value);
            setLogoPreview(fileUrl);
            setGeneralData(prev => ({ ...prev, logo: value, logoPreview: fileUrl }));
            return;
        }
        setGeneralData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        try {
            const data = new FormData();
            Object.keys(generalData).forEach(key => {
                if (key === 'logo') {
                    if (logo) data.append('logo', logo);
                } else {
                    data.append(key, generalData[key]);
                }
            });
            const res = await api.put(`/api/companies/${id}/general`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            const updated = res.data;
            setGeneralData(prev => ({ ...prev, ...updated, logoPreview: updated.logo || '' }));
            setLogoPreview(updated.logo || '');
            setLogo(null);
            setSavedModalTitle('Update successful');
            setSavedModalMessage('General company information was updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating general information:', err);
            return false;
        }
    };

    const handleDeleteCompany = () => setShowDeleteModal(true);

    const confirmDeleteCompany = async () => {
        try {
            await api.delete(`/api/companies/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-company-list');
        } catch (err) {
            console.error('Error deleting company:', err);
        }
    };

    const toggleActivityStatus = async () => {
        try {
            const updatedStatus = !activityStatus;
            await api.patch(`/api/companies/${id}/activity-status`, { activityStatus: updatedStatus });
            setActivityStatus(updatedStatus);
            setStatusMessage(updatedStatus ? 'Company Activated' : 'Company Deactivated');
            setShowStatusModal(true);
            setGeneralData(prev => ({ ...prev, activityStatus: updatedStatus }));
        } catch (err) {
            console.error('Error toggling activity status:', err);
        }
    };

    const handleResetLogo = () => {
        setLogo(null);
        setLogoPreview(originalLogoUrl);
    };

    const handleDeleteImage = () => {
        setLogo(null);
        setLogoPreview('fallback');
        setGeneralData(prev => ({ ...prev, logo: '', logoPreview: 'fallback' }));
    };

    const handleExportPdf = async () => {
        try {
            await exportCompanyPdf(id);
        } catch (err) {
            console.error('Error exporting company PDF:', err);
        }
    };

    const flatRows = useMemo(() => {
        const rows = [];
        history.forEach(entry => {
            const changedBy = entry.changedBy;
            const name = changedBy
                ? `${changedBy.firstName || ''} ${changedBy.familyName || ''}`.trim() || '—'
                : '—';
            const changes = entry.changes?.length ? entry.changes : [{ field: '', oldValue: '', newValue: '' }];
            changes.forEach(c => {
                rows.push({ entryId: entry._id, date: entry.createdAt, section: entry.section, field: c.field, oldValue: c.oldValue, newValue: c.newValue, name });
            });
        });
        return rows;
    }, [history]);

    const totalPages = Math.ceil(flatRows.length / rowsPerPage);
    const currentRows = flatRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const o = []; for (let i = s; i <= e; i++) o.push(i); return o; };
        const boundary = 1, sibling = 2;
        const startPages = range(1, Math.min(boundary, totalPages));
        const endPages = range(Math.max(totalPages - boundary + 1, boundary + 1), totalPages);
        const siblingsStart = Math.max(Math.min(currentPage - sibling, totalPages - boundary - sibling * 2 - 1), boundary + 2);
        const siblingsEnd = Math.min(Math.max(currentPage + sibling, boundary + sibling * 2 + 2), totalPages - boundary - 1);
        const items = [
            ...startPages,
            ...(siblingsStart > boundary + 2 ? ['ellipsis'] : boundary + 1 < totalPages - boundary ? [boundary + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundary - 1 ? ['ellipsis'] : totalPages - boundary > boundary ? [totalPages - boundary] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityName} companyName={generalData.title} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleGeneralSubmit}>
                                                            <CompanyAddGeneral
                                                                formData={generalData}
                                                                handleChange={handleGeneralChange}
                                                                logo={generalData.logoPreview}
                                                                errors={errors}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                isEditPage={true}
                                                                activityStatus={activityStatus}
                                                                handleResetLogo={handleResetLogo}
                                                                forceRerenderKey={forceRerenderKey}
                                                                setForceRerenderKey={setForceRerenderKey}
                                                                handleDeleteImage={handleDeleteImage}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={companyNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <h5 className="mb-4">History of changes</h5>
                                                        {isLoadingHistory ? (
                                                            <p>Loading history...</p>
                                                        ) : flatRows.length === 0 ? (
                                                            <p className="text-muted">No changes recorded yet.</p>
                                                        ) : (
                                                            <>
                                                                <Table hover responsive>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>#</th>
                                                                            <th>Date & Time</th>
                                                                            <th>Section</th>
                                                                            <th>Field</th>
                                                                            <th>Old Value</th>
                                                                            <th>New Value</th>
                                                                            <th>Changed by</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {currentRows.map((row, idx) => (
                                                                            <tr key={`${row.entryId}-${idx}`}>
                                                                                <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                                                                <td>{formatDate(row.date)}</td>
                                                                                <td>{row.section}</td>
                                                                                <td>{FIELD_LABELS[row.field] || row.field || '—'}</td>
                                                                                <td>{row.oldValue || '—'}</td>
                                                                                <td>{row.newValue || '—'}</td>
                                                                                <td>{row.name}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                                <Row className="pagination-wrapper mt-4">
                                                                    <Col className="d-flex align-items-center">
                                                                        <span>Total {flatRows.length} {flatRows.length === 1 ? 'change' : 'changes'}</span>
                                                                        <Pagination>
                                                                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                                                                <BsChevronLeft />
                                                                            </Pagination.Prev>
                                                                            {getPaginationItems().map((item, i) =>
                                                                                item === 'ellipsis'
                                                                                    ? <Pagination.Ellipsis key={`e-${i}`} disabled />
                                                                                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item}</Pagination.Item>
                                                                            )}
                                                                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                                                                <BsChevronRight />
                                                                            </Pagination.Next>
                                                                        </Pagination>
                                                                    </Col>
                                                                    <Col className="pagination-controls">
                                                                        <div className="pagination-controls-per-page">
                                                                            <Form.Select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                                                <option value="10">10 / page</option>
                                                                                <option value="20">20 / page</option>
                                                                                <option value="40">40 / page</option>
                                                                            </Form.Select>
                                                                        </div>
                                                                        <label className="pagination-controls-goto-label" htmlFor="history-goto-page">Go to</label>
                                                                        <Form.Control
                                                                            id="history-goto-page"
                                                                            className="pagination-controls-goto-input"
                                                                            type="number"
                                                                            value={currentPage}
                                                                            onChange={e => { const p = Number(e.target.value); if (p >= 1 && p <= totalPages) setCurrentPage(p); }}
                                                                            onBlur={() => { if (currentPage < 1) setCurrentPage(1); if (currentPage > totalPages) setCurrentPage(totalPages); }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-danger" onClick={handleDeleteCompany} className="px-4">
                                Delete
                            </Button>
                            <Button variant="outline-secondary" onClick={toggleActivityStatus} className="px-4">
                                {activityStatus ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download company report
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this company? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="outline-danger" onClick={confirmDeleteCompany}>Delete</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Status Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{statusMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowStatusModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image src={icSucess} alt="" className="d-block m-auto mb-4" />
                    <h2 className="text-center mb-3">{savedModalTitle}</h2>
                    <p className="text-center">{savedModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CompanyEditHistory;
